/* eslint-disable no-unused-vars */

import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { multiBranchFeat } from 'src/constants/feature-toggle';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import LocalStorageService from 'src/services/LocalStorageService';

const { actions: userActions, selectors: userSelectors } = slices.user;

const BRANCH_NICKNAME_BY_ID = new Map<number, string>([
  [1, 'EARNSHAW'],
  [2, 'LPC'],
  [4, 'EXPERIENCE']
]);

const isSessionValid = () => {
  const userInfo = LocalStorageService.getItem('userInfo');
  const expiredAt =
    userInfo?.expired_at ||
    userInfo?.expires_at ||
    getMidnightAfterLogin(userInfo?.loggedInTime);

  if (!userInfo?.id) {
    return false;
  }

  const expiredAtTime = new Date(expiredAt).getTime();

  if (Number.isNaN(expiredAtTime)) {
    return false;
  }

  return expiredAtTime > Date.now();
};

const getMidnightAfterLogin = (loggedInTime?: number) => {
  if (!loggedInTime) {
    return undefined;
  }

  const loggedInDate = new Date(loggedInTime);
  const midnightAfterLogin = new Date(loggedInDate);
  midnightAfterLogin.setHours(24, 0, 0, 0);

  return midnightAfterLogin.toISOString();
};

const useUserInfo = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { pathname } = useLocation();

  const userDetails = useAppSelector(userSelectors.selectUserInfo);
  const favoriteBranch = useAppSelector(userSelectors.selectFavoriteBranch);
  const userBranches = userDetails?.branches;
  const userBranchIds = userDetails?.branches?.map((b) => b.id);

  const isAccountingStaff = useMemo(
    () =>
      !!userDetails?.all_roles?.find(
        (role) => role.name === 'Accounting Staff'
      ),
    [userDetails]
  );

  const permissionNamesOnly = useMemo(
    () =>
      userDetails?.all_permissions?.map(
        (permission) => permission?.name || ''
      ) || [],
    [userDetails]
  );

  const checkUserTokenV2 = useCallback(async () => {
    return isSessionValid();
  }, []);

  const checkUserToken = useCallback(async () => {
    if (pathname === '/login') {
      return;
    }

    if (!isSessionValid()) {
      dispatch(userActions.clearSession());
      window.location.href = '/login';
      return;
    }
  }, [dispatch, pathname]);

  const setFavoriteBranch = (id: number | null) => {
    dispatch(userActions.setFavoriteBranch(id));
  };

  const getUserDetails = useCallback(async () => {
    if (!multiBranchFeat) {
      return;
    }

    if (id) {
      const user_id = +id;
      if (isNaN(user_id)) {
        return;
      }
      const response = unwrapResult(
        await dispatch(userActions.getUserThunk(user_id))
      );
      if (response.success && response.originalData?.user) {
        dispatch(userActions.updateAccountInfo(response.originalData?.user));
      }
    }
  }, [dispatch, id]);

  const userBranchOptions = useMemo(() => {
    return (
      userBranches?.map((b) => ({
        name: b.branch_name,
        value: b.id
      })) || []
    );
  }, [userBranches]);

  const allBranchIDs = useMemo(() => {
    return userBranchOptions?.map((i) => i.value) ?? [];
  }, [userBranchOptions]);

  const userBranchNameViaId = useCallback(
    (id?: number | null) => {
      return userBranchOptions?.find((b) => b.value === id)?.name;
    },
    [userBranchOptions]
  );

  const defaultValueBranch = useMemo(() => {
    const branchIds: number[] = userBranchOptions.map(
      (branch) => branch?.value
    );
    return branchIds.length > 0 ? [branchIds[0]] : [];
  }, [userBranchOptions]);

  const userBranchIdDefaultOption = useMemo(() => {
    if (userBranches && userBranches?.length > 0) {
      return userBranches[0].id;
    }
    return undefined;
  }, [userBranches]);

  const userBranchNicknames = useMemo(
    () =>
      userBranches
        ?.map((b) => BRANCH_NICKNAME_BY_ID.get(b.id))
        .filter(Boolean) ?? [],
    [userBranches]
  );

  return {
    id,
    userBranchIds,
    userDetails,
    favoriteBranch,
    userBranches,
    allBranchIDs,
    userBranchOptions,
    userBranchIdDefaultOption,
    defaultValueBranch,
    permissionNamesOnly,
    isAccountingStaff,
    userBranchNicknames,

    userBranchNameViaId,
    checkUserToken,
    setFavoriteBranch,
    getUserDetails,
    checkUserTokenV2
  };
};

export default useUserInfo;
